import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types/api';
import { apiService } from '../services/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  isAuthenticated: boolean;
  setIntendedDestination: (path: string) => void;
  getIntendedDestination: () => string | null;
  clearIntendedDestination: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          
          // Verify token is still valid
          await apiService.getProfile();
        } catch (error) {
          // Token is invalid, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const setIntendedDestination = (path: string) => {
    sessionStorage.setItem('intendedDestination', path);
  };

  const getIntendedDestination = (): string | null => {
    return sessionStorage.getItem('intendedDestination');
  };

  const clearIntendedDestination = () => {
    sessionStorage.removeItem('intendedDestination');
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await apiService.login({ email, password });
      const { token: newToken } = response;
      
      setToken(newToken);
      localStorage.setItem('token', newToken);
      
      // Fetch full user profile with permissions
      const fullUser = await apiService.getProfile();
      setUser(fullUser);
      localStorage.setItem('user', JSON.stringify(fullUser));
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearIntendedDestination();
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    loading,
    isAuthenticated: !!token && !!user,
    setIntendedDestination,
    getIntendedDestination,
    clearIntendedDestination,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
